'use client';

import { useRef, useEffect } from 'react';
import Link from 'next/link';
import { gsap } from '@/lib/gsap';

const policyContent = [
    {
        id: 'coppa-compliance',
        title: 'COPPA Compliance',
        content: `Little Canvas is designed for children (ages 4+) and we are committed to protecting their privacy. We are fully compliant with the Children's Online Privacy Protection Act (COPPA). We do not knowingly collect any personal information from children under the age of 13.`,
    },
    {
        id: 'information-collection',
        title: 'No Personal Data Collection',
        content: `We do not collect any personal information. The app does not require user registration or accounts. We do not use analytics, tracking, or advertisements. We do not transmit any data over the internet.`,
    },
    {
        id: 'device-permissions',
        title: 'Device Permissions',
        content: `Little Canvas requests access to your device's Photo Library only when you initiate specific actions: 1) Read Access: To allow you to select a photo to use as a drawing background. 2) Write Access: To allow you to save your drawings to the Photo Library. Granting these permissions is optional and can be managed in your device settings.`,
    },
    {
        id: 'data-retention',
        title: 'Data Retention',
        content: `All drawings and data are stored locally on your device. We do not store any of your data on external servers. If you uninstall the app, the data stored within the app will be deleted. Any drawings you have exported to your Photo Library will remain there.`,
    },
    {
        id: 'data-sharing',
        title: 'No Third-Party Sharing',
        content: `Since we do not collect any personal information, we do not share, sell, or trade any data with third parties. The app does not utilize any third-party services or SDKs that collect user data.`,
    },
    {
        id: 'parent-info',
        title: 'For Parents and Guardians',
        content: `We encourage parents and guardians to take an active role in their children's online activities. If you have any questions or concerns about our privacy practices or your child's use of the app, please contact us.`,
    },
    {
        id: 'changes',
        title: 'Changes to This Policy',
        content: `We may update this Privacy Policy from time to time. Any changes will be effective immediately upon posting the updated policy on this page. We encourage you to review this policy periodically.`,
    },
];

export function PrivacyPolicyClient() {
    const pageRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    // Hover effect for title letters
    const applyHoverEffect = (letters: NodeListOf<Element>) => {
        const spansArray = Array.from(letters);

        const handleMouseMove = (e: Event) => {
            const mouseEvent = e as MouseEvent;
            const hoveredSpan = mouseEvent.target as HTMLElement;
            const rect = hoveredSpan.getBoundingClientRect();
            const mouseX = mouseEvent.clientX - rect.left;
            const scaleFactor = 0.2;
            const center = rect.width / 2;

            let scale: number;
            if (mouseX < center) {
                scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
            } else {
                scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
            }

            gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

            const hoveredIndex = spansArray.indexOf(hoveredSpan);
            const prevSpan = spansArray[hoveredIndex - 1];
            const nextSpan = spansArray[hoveredIndex + 1];

            if (prevSpan) {
                const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
                const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
                gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
            }

            if (nextSpan) {
                const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
                const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
                gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
            }
        };

        const handleMouseLeave = () => {
            spansArray.forEach((span) => {
                gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
            });
        };

        spansArray.forEach((span) => {
            span.addEventListener('mousemove', handleMouseMove);
            span.addEventListener('mouseleave', handleMouseLeave);
        });
    };

    useEffect(() => {
        if (!pageRef.current) return;

        const ctx = gsap.context(() => {
            // Animate title letters - same as home page
            const letters = titleRef.current?.querySelectorAll('.letter');
            if (letters) {
                gsap.set(letters, { scaleY: 0.3, opacity: 0 });

                const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);
                const letterTimeline = gsap.timeline({ delay: 0.5 });

                shuffledLetters.forEach((letter, index) => {
                    letterTimeline.to(letter, {
                        duration: 0.6,
                        scaleY: 1,
                        opacity: 1,
                        ease: 'power3.out',
                    }, index * 0.05);
                });

                // Apply hover effect after animation
                letterTimeline.call(() => {
                    applyHoverEffect(letters);
                });
            }

            // Animate content sections
            if (contentRef.current) {
                const sections = contentRef.current.querySelectorAll('.policy-section');
                sections.forEach((section, index) => {
                    gsap.set(section, { y: 30, opacity: 0 });
                    gsap.to(section, {
                        scrollTrigger: {
                            trigger: section,
                            start: 'top 90%',
                        },
                        y: 0,
                        opacity: 1,
                        duration: 0.5,
                        delay: index * 0.03,
                        ease: 'power3.out',
                    });
                });
            }
        }, pageRef);

        return () => ctx.revert();
    }, []);

    return (
        <div ref={pageRef} style={{ backgroundColor: '#0c0c0c' }}>
            {/* Hero Section */}
            <section
                className="relative flex flex-col items-center justify-center text-center"
                style={{
                    backgroundColor: '#0c0c0c',
                    paddingTop: '180px',
                    paddingBottom: '100px',
                }}
            >
                <div className="content-padding w-full">
                    <h1
                        ref={titleRef}
                        className="hero-title font-six-caps text-[calc(1rem+20vw)] leading-[0.9] text-white lowercase"
                    >
                        {'PRIVACY POLICY'.split('').map((char, i) => (
                            <span
                                key={i}
                                className={`letter inline-block ${char === ' ' ? 'mx-[2vw]' : ''}`}
                                style={{ transformOrigin: 'center bottom' }}
                            >
                                {char === ' ' ? '\u00A0' : char}
                            </span>
                        ))}
                    </h1>
                </div>

                {/* Description and date - Left aligned */}
                <div className="content-padding w-full" style={{ marginTop: '80px' }}>
                    <div className="max-w-4xl mx-auto">
                        <div className="max-w-md">
                            <p className="text-white/50 text-lg leading-relaxed text-left">
                                Privacy policy for Little Canvas - The drawing app designed for children. Safe, simple, and private.
                            </p>
                            <p className="text-white/30 text-sm text-left mt-4">
                                Effective Date: January 16, 2026
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section style={{ backgroundColor: '#0c0c0c', paddingBottom: '80px' }}>
                <div className="content-padding">
                    <div className="max-w-3xl mx-auto" ref={contentRef}>

                        {/* About Little Canvas */}
                        <div className="policy-section pb-24 mb-24" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            <h2 className="text-2xl font-semibold text-white mb-6">
                                About Little Canvas
                            </h2>
                            <p className="text-white/60 text-lg leading-loose">
                                Little Canvas is a drawing app built for creativity. We believe that children&apos;s digital spaces should be free from tracking, advertising, and data collection. This privacy policy explains our commitment to keeping your child&apos;s data safe—by not collecting it at all.
                            </p>
                        </div>

                        {/* Policy Sections */}
                        {policyContent.map((section, index) => (
                            <div
                                key={section.id}
                                id={section.id}
                                className="policy-section pb-20 mb-20"
                                style={{
                                    borderBottom: index < policyContent.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                                }}
                            >
                                <div className="flex items-start gap-8">
                                    <span
                                        className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-base font-semibold"
                                        style={{ backgroundColor: '#173835', color: '#fff' }}
                                    >
                                        {index + 1}
                                    </span>
                                    <div className="flex-1">
                                        <h2 className="text-2xl font-semibold text-white mb-4">
                                            {section.title}
                                        </h2>
                                        <p className="text-white/60 text-lg leading-loose">
                                            {section.content}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}

                    </div>
                </div>
            </section>

            {/* Contact Section - Green Background, Centered */}
            <section
                style={{
                    backgroundColor: '#173835',
                    paddingTop: '100px',
                    paddingBottom: '100px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    <h2
                        className="font-six-caps text-[calc(1rem+10vw)] md:text-[calc(1rem+8vw)] leading-[0.9] text-white lowercase"
                        style={{ textAlign: 'center', marginBottom: '32px' }}
                    >
                        Contact
                    </h2>
                    <p
                        className="text-white/70 text-lg leading-relaxed"
                        style={{ textAlign: 'center', maxWidth: '500px', marginBottom: '48px', padding: '0 24px' }}
                    >
                        If you have any questions about this Privacy Policy, please contact us.
                    </p>

                    {/* Contact Info - Centered */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '24px',
                            marginBottom: '48px',
                        }}
                        className="sm:flex-row sm:gap-10"
                    >
                        <a
                            href="mailto:privacy@datacode.app"
                            className="flex items-center gap-3 text-white hover:text-white/80 transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span>privacy@datacode.app</span>
                        </a>

                        <span className="hidden sm:block text-white/30">|</span>

                        <div className="flex items-center gap-3 text-white/70">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>Iraq, Erbil, MRF B2, apt80</span>
                        </div>
                    </div>

                    {/* Back to Home - Centered */}
                    <Link
                        href="/"
                        className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm uppercase tracking-wider"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to Home
                    </Link>
                </div>
            </section>
        </div>
    );
}
